import { Button } from '@mui/material';
import { useMemo, useState } from 'react';
import CustomModal, { CustomModalBasicProps } from 'src/components/CustomModal';
import { cleanSearchInput } from 'src/utils/string';
import FhirOrganizationGrid, { ActionsFn, filterOrgBySearchInput } from './FhirOrganizationGrid';
import { useOrganizations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useGridFilters from 'src/careflow/ph-core/grid/hook/useGridFilters';

export interface ChildModalProps extends CustomModalBasicProps {
  filterIdsOrg: string[];
  handleAction: Function;
  translate: any;
}

const FhirOrganizationListModal = ({
  open,
  filterIdsOrg,
  handleAction,
  translate,
  onClose,
  ...modalProps
}: ChildModalProps) => {
  const [organizations, { refresh: refreshOrg, isLoading: isLoadingOrg }] = useOrganizations({
    filter: { 'active:not': false },
    map: OrganizationWrapper,
    autofetch: !!open,
  });

  const [orgSelected, setOrgSelected] = useState<WrappedOrganization[] | null>(null);

  const allowSelect = checkAclValidation({ acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CONTACTS.ADD] });

  const { searchTextFieldValue, onSearchTextFieldChange } = useGridFilters({
    searchTextFieldValue: null,
  });

  const handleOrgSelection = (org: WrappedOrganization, isSelected: boolean) => {
    if (isSelected) {
      setOrgSelected((prev) => prev!.filter((e) => e?.id !== org?.id));
    } else {
      setOrgSelected((prev) => [...(prev ?? []), org]);
    }
  };

  const orgsFiltered = useMemo(() => {
    const searchInput = cleanSearchInput(searchTextFieldValue ?? '').toLowerCase();

    const filterSet = new Set(filterIdsOrg ?? []);

    return (organizations ?? [])
      .filter((org) => !filterSet.has(org.id!))
      .filter((org) => filterOrgBySearchInput(searchInput, org));
  }, [organizations, filterIdsOrg, searchTextFieldValue]);

  const handleSave = async () => {
    await handleAction(orgSelected);
    await refreshOrg();
  };

  const handleClose = () => {
    onSearchTextFieldChange('');
    setOrgSelected(null);
    onClose?.({}, 'backdropClick');
  };

  const actions: ActionsFn = ({ row }) => {
    const isOrgSelected = !!orgSelected?.find((org) => org?.id === row?.id);

    return [
      allowSelect ? (
        <Button
          onClick={() => handleOrgSelection(row, isOrgSelected)}
          color={isOrgSelected ? 'error' : 'primary'}
          variant="outlined"
        >
          {isOrgSelected ? translate('details.child.remove') : translate('details.child.select')}
        </Button>
      ) : (
        <></>
      ),
    ];
  };

  return (
    <CustomModal
      open={open}
      title={translate('details.child.title')}
      breadcrumbs={['']}
      isLoading={isLoadingOrg}
      onSave={handleSave}
      isSaveButtonDisabled={orgSelected === null || orgSelected?.length === 0}
      onCancel={handleClose as Function}
      onClose={handleClose}
      containerSx={[{ overflow: 'scroll', width: '70vw' }]}
      {...modalProps}
    >
      <FhirOrganizationGrid
        organizations={orgsFiltered}
        searchTextFieldValue={searchTextFieldValue}
        onSearchTextFieldChange={onSearchTextFieldChange}
        translate={translate}
        actions={actions}
      />
    </CustomModal>
  );
};

export default FhirOrganizationListModal;
